import React, { useMemo } from "react";
import { Button } from "@pd/pd-ui-kit";
import {
  Control,
  Controller,
  useFieldArray,
  UseFormMethods,
} from "react-hook-form";
import { useTranslation } from "react-i18next";

import {
  defaultValuePosition,
  getNextPositionSlotIndex,
} from "~/pages/BaseStaff/EmployeeProfileEditor/EmployeeProfileForm/model/config";
import {
  IEmployeeProfileForm,
  TPositionFormItem,
} from "~/pages/BaseStaff/EmployeeProfileEditor/EmployeeProfileForm/model/types";
import { useCardEditAccess } from "~/pages/BaseStaff/EmployeeProfileEditor/EmployeeProfileForm/model/useCardEditAccess";

import FormDateField from "./FormDateField";
import FormField from "./FormField";
import PositionItem from "./PositionItem";

import s from "~/pages/BaseStaff/EmployeeProfileEditor/EmployeeProfileForm/EmployeeProfileForm.less";

interface IPositionsSectionProps {
  control: Control<IEmployeeProfileForm>;
  errors: UseFormMethods<IEmployeeProfileForm>["errors"];
  setValue: UseFormMethods<IEmployeeProfileForm>["setValue"];
  watch: UseFormMethods<IEmployeeProfileForm>["watch"];
  licensesLoaded: boolean;
  onRequestLicense: () => void;
  refetch?: () => void;
}

const PositionsSection = ({
  control,
  errors,
  setValue,
  watch,
  licensesLoaded,
  onRequestLicense,
  refetch,
}: IPositionsSectionProps) => {
  const { t } = useTranslation();
  const { canEditAll } = useCardEditAccess();

  const { fields, append, remove } = useFieldArray({
    control,
    name: "positions",
  });

  const nextPositionSlotIndex = useMemo(
    () => getNextPositionSlotIndex(fields as unknown as TPositionFormItem[]),
    [fields],
  );

  return (
    <section className={s.section}>
      <div className={s.laborHeader}>
        <h2 className={`${s.sectionTitle} ${s.laborHeaderTitle}`}>
          {t("employee.listTitleLaborActivity")}
        </h2>
        <FormDateField
          name="hiredDate"
          control={control}
          label={t("employee.HiredDate")}
          disabled={!canEditAll}
          className={s.laborHeaderField}
          error={errors.hiredDate?.message}
          required
        />
      </div>

      {fields.map((field, index) => {
        const isNew = Boolean((field as TPositionFormItem).isNew);

        return (
          <PositionItem
            key={field.id}
            index={index}
            positionNumber={(field as TPositionFormItem).number}
            control={control}
            errors={errors}
            setValue={setValue}
            watch={watch}
            licensesLoaded={licensesLoaded}
            onRequestLicense={onRequestLicense}
            onRemove={isNew ? () => remove(index) : undefined}
            isNew={isNew}
            refetch={refetch}
          />
        );
      })}

      {canEditAll && nextPositionSlotIndex !== null && (
        <div className={s.addPositionBtn}>
          <Button
            color="secondary"
            fontSize="14"
            size="medium"
            radius={5}
            onClick={() => append(defaultValuePosition(nextPositionSlotIndex))}
          >
            {t("employee.addPosition")}
          </Button>
        </div>
      )}
    </section>
  );
};

export default PositionsSection;
